import { getGeoInfo, signupApi, suggestTenantNameApi } from '@tools/api/api';
import { I18nStore } from '@tools/i18n/store';
import { injectTSDI } from '@tools/tsdi';
import { isArray } from '@tools/utils/array';
import { getQueryParams } from '@tools/utils/url';
import { FieldState, FormState } from 'formstate';
import { makeAutoObservable } from 'mobx';
import { toast } from 'react-toastify';
import { component } from 'tsdi';

import { SignupStore } from '../signup-store';

export interface SignupFormModel {
    email: string;
    password: string;
    studioName: string;
    country: string;
    contactOptIn: boolean;
    token?: string;
}
export interface BackendError {
    args: string[];
    errorCode: string;
    errorMessage: string;
    typedArgs: string[];
}

const emailRegex =
    /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).{8,64}$/;

@component
export class Step2Store {
    private get i18n() {
        return injectTSDI(I18nStore);
    }

    constructor() {
        makeAutoObservable(this);
    }
    public domain = 'studioname';
    public isDomainSuggestionLoading = false;
    public widgetToken!: string;

    public form = new FormState({
        email: new FieldState('')
            .validators(
                (val) =>
                    !val && this.i18n.translate('commons.validation.required'),
                (val) =>
                    !emailRegex.test(val) &&
                    this.i18n.translate('signup.email.error')
            )
            .disableAutoValidation(),
        password: new FieldState('')
            .validators(
                (val) =>
                    !val && this.i18n.translate('commons.validation.required'),
                (val) =>
                    !passwordRegex.test(val) &&
                    this.i18n.translate(
                        'errormessage.password.incorrect.characters.or.length'
                    )
            )
            .disableAutoValidation(),
        phone: new FieldState(''),
        studioName: new FieldState('')
            .validators(
                (val) =>
                    !val && this.i18n.translate('commons.validation.required'),
                (val) =>
                    val.length < 3 &&
                    this.i18n.translate('signup.studioname.error')
            )
            .disableAutoValidation(),
        country: new FieldState('').validators(
            (val) => !val && this.i18n.translate('signup.country.error')
        ),
        contactOptIn: new FieldState(false),
        processingOptIn: new FieldState(false).validators(
            (val) =>
                !val && this.i18n.translate('signup.processing.consent.error')
        )
    });

    public suggestDomainName = async (studioName?: string) => {
        if (!studioName) {
            this.domain = 'studioname';
            return;
        }
        this.isDomainSuggestionLoading = true;
        try {
            const { tenantName } = await suggestTenantNameApi({
                studioOrCompanyName: studioName,
                tenantUsageType: 'DEMO'
            });
            this.domain = tenantName;
        } catch (e) {
            console.error('Error while suggesting tenant name', e);
            this.domain = studioName
                .replace(/\s/g, '')
                .replace(/[^A-Za-z0-9-]/g, '')
                .toLocaleLowerCase();
        } finally {
            this.isDomainSuggestionLoading = false;
        }
    };

    public onNextStep = async () => {
        const signupStore = injectTSDI(SignupStore);

        const {
            phone,
            email,
            password,
            studioName,
            country,
            contactOptIn,
            processingOptIn
        } = this.form.$;

        studioName.value = studioName.value.trim();

        const res = await this.form.validate();
        if (res.hasError) {
            return;
        }

        const phoneValue = phone.value;
        const { widgetToken } = this;
        // eslint-disable-next-line no-undef
        const scoreToken = await grecaptcha.execute(
            process.env.RECAPTCHAV3_SITEKEY,
            {
                action: 'checkifbot'
            }
        );
        const geoInfo = await getGeoInfo();
        const countryFromIP = geoInfo?.split(';')?.pop() || '';
        const values = {
            email: email.value,
            password: password.value,
            ...(phoneValue && { phone: phoneValue }),
            studioName: studioName.value,
            domain: this.domain,
            country: country.value,
            contactOptIn: contactOptIn.value || false,
            processingOptIn: processingOptIn.value,
            scoreToken,
            ...this.getUtmAttributes(),
            ...(countryFromIP && { countryFromIP }),
            ...(widgetToken && { widgetToken })
        };

        try {
            const { tenantLoginUrl } = await signupApi(values);
            this.sendAnalyticsEvents(values);
            if (tenantLoginUrl) {
                signupStore.setTenantURL(tenantLoginUrl);
                signupStore.goToNextStep();
            }
        } catch (err) {
            this.handleSignupError(err as BackendError[]);
        }
    };

    private getUtmAttributes = () => {
        const searchEngines = [
            'google',
            'bing',
            'duckduckgo',
            'yahoo',
            'yandex',
            'aol'
        ];
        const utmCampaign = getQueryParams('utm_campaign') ?? '';
        const utmSource = getQueryParams('utm_source') ?? document.referrer;
        const utmMedium =
            getQueryParams('utm_medium') ??
            (searchEngines.some((engine) => {
                if (document.referrer.toLocaleLowerCase().includes(engine)) {
                    return true;
                }
                return false;
            })
                ? 'referral'
                : 'organic');
        const utmTerm = getQueryParams('utm_term') ?? '';
        return { utmCampaign, utmSource, utmMedium, utmTerm };
    };

    private sendAnalyticsEvents = (values: SignupFormModel) => {
        const { sendEvent } = injectTSDI(SignupStore);
        const { contactOptIn, country } = values;

        if (contactOptIn) {
            sendEvent('contact_optin');
        }
        sendEvent('submit_registration', {
            country: this.parseCountryName(country)
        });
        window?.fbq('track', 'CompleteRegistration');
        window?.uetq.push('event', 'submit', { event_category: 'Demo submit' });
    };

    private parseCountryName = (value: string) => {
        switch (value) {
            case 'ES':
                return 'spain';
            case 'DE':
                return 'germany';
            case 'FR':
                return 'france';
            case 'AT':
                return 'austria';
            case 'IT':
                return 'italy';
            case 'NL':
                return 'netherlands';
            case 'UK':
                return 'united_kingdom';
            case 'CH':
                return 'switzerland';
            default:
                return '';
        }
    };

    private handleSignupError = (err: BackendError[]) => {
        const errorCode = this.getErrorCode(err);
        const { studioName } = this.form.$;
        const { translate } = this.i18n;
        switch (errorCode) {
            case 'errormessage.tenantidentifier.already.exists':
                studioName.setError(this.i18n.translate('signup.domain.taken'));
                break;
            case 'errormessage.tenantname.invalid':
                toast(translate('signup.domain.generic.error'), {
                    type: 'error'
                });
                studioName.setError(
                    translate('errormessage.tenantname.invalid')
                );
                break;
            case 'errormessage.invalid.recaptcha.token':
                toast(translate('errormessage.invalid.recaptcha.token'), {
                    type: 'error'
                });
                break;
            default:
                toast(translate('commons.error'), { type: 'error' });
        }
    };

    private getErrorCode = (err: BackendError[]) => {
        if (isArray(err)) {
            return err[0]?.errorCode;
        }
        return null;
    };
}
